import { Helmet } from 'react-helmet';
import { CSVLink } from 'react-csv';
import Header from '../Header/Header';
import Tab from '../Tab/Tab';
import Form from '../Form/Form';
import useSystemID from '../../hooks/useSystemID';
import useAPIData from '../../hooks/useAPIData';
import useMsalContactData from '../../hooks/useMsalContactData';
import useMetadata from '../../hooks/useMetadata';
import useSelectedData from '../../hooks/useSelectedData';
import useParkingLot from '../../hooks/useParkingLot';
import useInitialStatus from '../../hooks/useInitialStatus';
import useSelectedStatus from '../../hooks/useSelectedStatus';
import useSelectedTab from '../../hooks/useSelectedTab';
import useFieldsChangedMonitor from '../../hooks/useFieldsChangedMonitor';
import config from '../../config';
import {
  saveProcessing,
  saveDetailedQC,
  saveGeospatialQC,
  saveReasonablenessQC,
  saveUSACEQC,
  saveUSACEActions,
  saveInitialQC,
  saveInitialAnalyst,
  saveFinalQC,
  saveParkingLot,
  deleteParkingLot,
  saveSystemStatus,
  formatDateString
} from './EditorHelpers';
import {
  useStreamlineContext,
  useSetStreamlineContext
} from '../../contexts/ContextProvider';
import { useEffect } from 'react';

let formName = '';
let formData = undefined;
let handleFormChange = undefined;

export default function Editor() {
  const systemID = useSystemID();
  const systemIDs = useAPIData('/nld_tracker/system_ids');
  const contactEmail = useMsalContactData('email');
  const contactRole = useMsalContactData('role');
  const contactData = useAPIData('/nld_tracker/contact?email=' + contactEmail);
  const isStreamline = useStreamlineContext();
  const setIsStreamline = useSetStreamlineContext();

  let activeTables = isStreamline
    ? config.streamlineTables
    : config.originalTables;

  const [metadata, handleRequestMetadata] = useMetadata(
    '/nld_tracker/editor/system_metadata?system_id=' + systemID
  );

  const [preProcessing] = useSelectedData(
    '/nld_tracker/editor/pre_processing?system_id=' + systemID
  );
  const [
    processing,
    processingChange,
    restProcessingChange,
    handleProcessingChange
  ] = useSelectedData('/nld_tracker/editor/processing?system_id=' + systemID);
  const [
    detailedQC,
    detailedQCChange,
    restDetailedQCChange,
    handleDetailedQCChange
  ] = useSelectedData('/nld_tracker/editor/detailed_qc?system_id=' + systemID);
  const [
    geospatialQC,
    geospatialQCChange,
    restGeospatialQCChange,
    handleGeospatialQCChange
  ] = useSelectedData(
    '/nld_tracker/editor/geospatial_qc?system_id=' + systemID
  );
  const [
    reasonablenessQC,
    reasonablenessQCChange,
    restReasonablenessQCChange,
    handleReasonablenessQCChange
  ] = useSelectedData(
    '/nld_tracker/editor/reasonableness_qc?system_id=' + systemID
  );
  const [usaceQC, usaceQCChange, restUSACEQCChange, handleUSACEQCChange] =
    useSelectedData('/nld_tracker/editor/usace_qc?system_id=' + systemID);
  const [
    usaceActions,
    usaceActionsChange,
    restUSACEActionsChange,
    handleUSACEActionsChange
  ] = useSelectedData(
    '/nld_tracker/editor/usace_actions?system_id=' + systemID
  );

  //-------------------START fetch streamline form data-------------------
  const [
    initialQC,
    initialQCChange,
    restInitialQCChange,
    handleInitialQCChange
  ] = useSelectedData('/nld_tracker/editor/initial_qc?system_id=' + systemID);
  const [
    initialAnalyst,
    initialAnalystChange,
    restInitialAnalystChange,
    handleInitialAnalystChange
  ] = useSelectedData(
    '/nld_tracker/editor/initial_analyst?system_id=' + systemID
  );
  const [finalQC, finalQCChange, restFinalQCChange, handleFinalQCChange] =
    useSelectedData('/nld_tracker/editor/final_qc?system_id=' + systemID);
  //-------------------END fetch streamline form data-------------------

  const [
    parkingLot,
    parkingLotChange,
    restParkingLotChange,
    handleParkingLotChange
  ] = useParkingLot('/nld_tracker/editor/parking_lot?system_id=' + systemID);

  const initialStatus = useInitialStatus(metadata);
  const [selectedStatus, handleStatusChange] = useSelectedStatus(initialStatus);

  const [selectedTab, handleTabChange] = useSelectedTab(initialStatus);

  useFieldsChangedMonitor({
    processingChange,
    detailedQCChange,
    geospatialQCChange,
    reasonablenessQCChange,
    initialQCChange,
    initialAnalystChange,
    finalQCChange
  });
  useEffect(() => {
    if (metadata?.hasOwnProperty('isStreamline')) {
      setIsStreamline(metadata.isStreamline === 'Yes');
    }
  }, [metadata, setIsStreamline]);

  useEffect(() => {
    if (!selectedTab || activeTables.includes(selectedTab)) return;

    if (isStreamline) {
      handleTabChange('Initial QC');
    } else {
      handleTabChange('Processing Details');
    }
  }, [isStreamline, activeTables, selectedTab, handleTabChange]);

  function saveChanges(tableName) {
    if (tableName === 'processing' && processingChange) {
      handleInitialAnalystChange(null);
      saveProcessing(systemID, contactData, processing, () => {
        restProcessingChange();
        restInitialAnalystChange();
      });
    } else if (tableName === 'detailedQC' && detailedQCChange) {
      saveDetailedQC(systemID, contactData, detailedQC, restDetailedQCChange);
    } else if (tableName === 'geospatialQC' && geospatialQCChange) {
      handleInitialAnalystChange(null);
      saveGeospatialQC(systemID, contactData, geospatialQC, () => {
        restGeospatialQCChange();
        restInitialAnalystChange();
      });
    } else if (tableName === 'reasonablenessQC' && reasonablenessQCChange) {
      handleInitialAnalystChange(null);
      handleFinalQCChange(null);
      saveReasonablenessQC(systemID, contactData, reasonablenessQC, () => {
        restReasonablenessQCChange();
        restInitialAnalystChange();
        restFinalQCChange();
      });
    } else if (tableName === 'usaceQC' && usaceQCChange) {
      saveUSACEQC(systemID, contactData, usaceQC, restUSACEQCChange);
    } else if (tableName === 'usaceActions' && usaceActionsChange) {
      saveUSACEActions(
        systemID,
        contactData,
        usaceActions,
        restUSACEActionsChange
      );
      //---------------------START streamline data change---------------------
    } else if (tableName === 'initialQC' && initialQCChange) {
      saveInitialQC(systemID, contactData, initialQC, restInitialQCChange);
    } else if (tableName === 'initialAnalyst' && initialAnalystChange) {
      // The rest and handle(null) will trigger refetch for those tabs,
      // So when user navigate to them after streamline, they'll be updated
      handleProcessingChange(null);
      handleGeospatialQCChange(null);
      handleReasonablenessQCChange(null);
      saveInitialAnalyst(systemID, contactData, initialAnalyst, () => {
        restInitialAnalystChange();
        restProcessingChange();
        restGeospatialQCChange();
        restReasonablenessQCChange();
      });
    } else if (tableName === 'finalQC' && finalQCChange) {
      handleReasonablenessQCChange(null);
      saveFinalQC(systemID, contactData, finalQC, () => {
        restFinalQCChange();
        restReasonablenessQCChange();
      });
    }
    //---------------------END streamline data change---------------------

    if (parkingLotChange) {
      if (parkingLot['Notes'] === '' && parkingLot['Reason'] === '') {
        if (
          selectedStatus !== undefined &&
          selectedStatus['Status'] === 'Parking Lot'
        ) {
          alert(
            'Please first select a status before clicking "Save Changes" and deleting the data in the Parking Lot table'
          );
        } else {
          deleteParkingLot(systemID, restParkingLotChange);
        }
      } else if (parkingLot['Notes'] !== '' && parkingLot['Reason'] === '') {
        alert(
          'Please specify a reason for why the system should be moved to the Parking Lot or delete the notes to indicate that the system should not be moved to the Parking Lot'
        );
      } else if (parkingLot['Notes'] === '' && parkingLot['Reason'] !== '') {
        alert(
          'Please include notes for why the system should be moved to the Parking Lot or set "Reason" to "Select" to indicate that the system should not be moved to the Parking Lot'
        );
      } else {
        saveParkingLot(systemID, contactData, parkingLot, restParkingLotChange);
      }
    }

    if (selectedStatus !== undefined && initialStatus !== selectedStatus) {
      if (
        selectedStatus['Detailed Status'] === 'USACE Complete' &&
        parkingLot['Reason'] !== ''
      ) {
        const parkingLotStatus = {
          Status: 'Parking Lot',
          'Detailed Status': parkingLot['Reason']
        };
        saveSystemStatus(
          systemID,
          contactData,
          parkingLotStatus,
          handleRequestMetadata
        );
      } else {
        if (
          selectedStatus === config.statusByKey.ready_for_review_detailed &&
          processing['Notes'] === ''
        ) {
          alert(
            'Please add notes to the Processing Details table before setting the status to "Complete"'
          );
        } else if (
          selectedStatus === config.statusByKey.ready_for_review_geospatial &&
          detailedQC['Notes'] === ''
        ) {
          alert(
            'Please add notes to the Detailed QC table before setting the status to "Complete"'
          );
        } else if (
          selectedStatus ===
            config.statusByKey.ready_for_review_reasonableness &&
          geospatialQC['Notes'] === ''
        ) {
          alert(
            'Please add notes to the Geospatial QC table before setting the status to "Complete"'
          );
        } else if (
          selectedStatus ===
            config.statusByKey.complete_usace_ready_for_review &&
          reasonablenessQC['Notes'] === ''
        ) {
          alert(
            'Please add notes to the Reasonableness QC table before setting the status to "Complete"'
          );
        } else {
          saveSystemStatus(
            systemID,
            contactData,
            selectedStatus,
            handleRequestMetadata
          );
        }
      }
    }
  }

  function makeCsvData() {
    return [
      ['SystemID', systemID],
      ['NLD System Type', reasonablenessQC['Type of System']],
      ['NLD Data Change Rationale', reasonablenessQC['Final Notes']],
      ['NLD Leveed Area Method', reasonablenessQC['Leveed Area Method']],
      ['NLD Data Source', reasonablenessQC['Elevation Source']],
      [
        'NLD Leveed Area Source Edit Date',
        reasonablenessQC['Last Saved']
          ? formatDateString(new Date(reasonablenessQC['Last Saved']))
          : ''
      ]
    ];
  }

  if (selectedTab === 'Pre-Processing Details') {
    formName = 'preProcessing';
    formData = preProcessing;
  } else if (selectedTab === 'Processing Details') {
    formName = 'processing';
    formData = processing;
    handleFormChange = handleProcessingChange;
  } else if (selectedTab === 'Detailed QC') {
    formName = 'detailedQC';
    formData = detailedQC;
    handleFormChange = handleDetailedQCChange;
  } else if (selectedTab === 'Geospatial QC') {
    formName = 'geospatialQC';
    formData = geospatialQC;
    handleFormChange = handleGeospatialQCChange;
  } else if (selectedTab === 'Reasonableness QC') {
    formName = 'reasonablenessQC';
    formData = reasonablenessQC;
    handleFormChange = handleReasonablenessQCChange;
  } else if (selectedTab === 'USACE QA') {
    formName = 'usaceQC';
    formData = usaceQC;
    handleFormChange = handleUSACEQCChange;
  } else if (selectedTab === 'USACE Actions') {
    formName = 'usaceActions';
    formData = usaceActions;
    handleFormChange = handleUSACEActionsChange;
  } else if (selectedTab === 'Initial QC') {
    //---------------START streamline forms---------------
    formName = 'initialQC';
    formData = initialQC;
    handleFormChange = handleInitialQCChange;
  } else if (selectedTab === 'Initial Analyst') {
    formName = 'initialAnalyst';
    formData = initialAnalyst;
    handleFormChange = handleInitialAnalystChange;
  } else if (selectedTab === 'Final QC') {
    formName = 'finalQC';
    formData = finalQC;
    handleFormChange = handleFinalQCChange;
    //---------------END streamline forms---------------
  }

  if (systemIDs && systemID) {
    if (systemIDs.includes(systemID)) {
      if (metadata && parkingLot && formData && contactData && contactRole) {
        const metaTableKeys = Object.keys(metadata).filter(
          (key) => key[0] === key[0].toUpperCase()
        );
        const metaTableData = Object.fromEntries(
          metaTableKeys.map((key) => [key, metadata[key]])
        );
        return (
          <div className='app'>
            <Helmet>
              <title>System Data · NLD Refinement</title>
            </Helmet>
            <Header titleParams={initialStatus} />
            <div className='body editor'>
              <div className='metadata-table'>
                <Form
                  tableName='systemMetadata'
                  data={metaTableData}
                  title='System Metadata'
                />
                <div>
                  <br></br>
                  <strong>Reminders:</strong>
                  <ul>
                    <li>
                      To save changes for a particular table, click 'Save
                      Changes'
                    </li>
                    <li>
                      Check the 'Date' and 'Contact' fields to confirm your
                      changes were saved
                    </li>
                    <li>
                      If you refresh or close the page, all unsaved changes will
                      be lost
                    </li>
                  </ul>
                </div>
              </div>
              <div className='input-table'>
                <div>
                  {activeTables.map((value, i, arr) => {
                    return (
                      <Tab
                        key={i}
                        value={value}
                        currentTab={selectedTab}
                        handleTabChange={handleTabChange}
                        inputStyle={
                          i === 0
                            ? { borderLeft: '0px', marginLeft: '0px' }
                            : i === arr.length - 1
                            ? { borderRight: '0px', marginRight: '0px' }
                            : {}
                        }
                      />
                    );
                  })}
                </div>
                <Form
                  tableName={formName}
                  data={formData}
                  handleFormChange={handleFormChange}
                  handleOnClick={saveChanges}
                  handleStatusChange={handleStatusChange}
                  selectedStatus={selectedStatus}
                  contactRole={contactRole}
                  descriptionWidth={'400px'}
                />
                {selectedTab === 'USACE Actions' && (
                  <div className='text-right'>
                    <CSVLink
                      data={makeCsvData()}
                      className='system-export-button'
                      filename={`nld_${systemID}_${config.getCurrentDateTime()}.csv`}
                    >
                      Export CSV
                    </CSVLink>
                  </div>
                )}
              </div>
              <div className='parkinglot'>
                {['preProcessing', 'usaceQC', 'usaceActions'].includes(
                  formName
                ) ||
                !config.tables?.[formName]['canUpdate'].includes(
                  contactRole
                ) ? (
                  <Form
                    tableName='uneditable'
                    data={parkingLot}
                    title='Parking Lot'
                  />
                ) : (
                  <Form
                    tableName='parkingLot'
                    data={parkingLot}
                    title='Parking Lot'
                    handleFormChange={handleParkingLotChange}
                  />
                )}
              </div>
            </div>
          </div>
        );
      }
      return <></>;
    }
    return (
      <div className='unauthorized-message'>
        <h2>The specified system ID of {systemID} is not in the database.</h2>
      </div>
    );
  }
  return <></>;
}
